import React from 'react';
import { Alert, Box, Divider, Typography } from '@mui/material';
import type { GlobalRuntimeSettings } from '../../../services/globalSettings.service';
import { GlobalSettingsNumberGrid } from '../../../pages/admin/GlobalSettingsNumberGrid';
import type { NumberSettingField } from '../../../pages/admin/global-settings.config';
import { billingPricingNumberFields } from '../../../pages/admin/global-settings.config';
import { isBillingPricingConfigValid } from './billingPricingValidation';

interface BillingPricingSectionProps {
  config: GlobalRuntimeSettings;
  onNumberChange: (field: NumberSettingField, value: number | undefined) => void;
}

export const BillingPricingSection: React.FC<BillingPricingSectionProps> = ({
  config,
  onNumberChange,
}) => {
  const invalid = (field: NumberSettingField) => {
    const value = config[field];
    if (!Number.isFinite(value)) return true;
    if (field === 'billingOverheadFactor') return value < 1;
    if (field === 'billingPaymentFeeRate' || field === 'billingTaxRate')
      return value < 0 || value >= 1;
    if (field === 'billingMinUtilization') return value <= 0 || value > 1;
    if (field === 'billingTargetMargin' || field === 'billingFxBuffer') return value < 0;
    if (field === 'billingCostVarianceMinAbsUsd' || field === 'billingFallbackImageUsd')
      return value < 0;
    if (field === 'billingCostVarianceThresholdPercent')
      return !Number.isInteger(value) || value < 0 || value > 100;
    return !Number.isInteger(value) || value <= 0;
  };
  const invalidField = billingPricingNumberFields.some(invalid);
  const invalidRates = !isBillingPricingConfigValid(config);

  return (
    <>
      <Divider className="my-6" />
      <Box className="mb-5">
        <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
          Billing & pricing
        </Typography>
        <Typography variant="body2" className="mt-1 text-gray-600 dark:text-slate-400">
          Cost assumptions and report limits used by billing estimates and cost monitoring.
        </Typography>
      </Box>
      {(invalidField || invalidRates) && (
        <Alert severity="error" className="mb-4">
          Check the billing values against their allowed ranges. Payment fee rate plus tax rate must
          be less than 1.
        </Alert>
      )}
      <GlobalSettingsNumberGrid
        config={config}
        fields={billingPricingNumberFields}
        columns={3}
        integer={false}
        integerForField={(field) =>
          field === 'billingCreditsPerUsd' ||
          field === 'billingCostVarianceThresholdPercent' ||
          field === 'billingReportMaxDays' ||
          field === 'billingUnitCostWindowDays'
        }
        inputProps={{ min: 0, step: 0.01 }}
        errorForField={invalid}
        onNumberChange={onNumberChange}
      />
      <Typography variant="caption" color="text.secondary" className="mt-3 block">
        FX rate comes from Exchange rate above.
      </Typography>
    </>
  );
};
